import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { IoMapOutline } from "react-icons/io5";
import { useAreaShapes } from "../../geo/areaShapes";
import type { AreaLike } from "../../geo/areaShapes";
import { shapesFromGeoJSON, thumbnailView } from "../../utils/areaThumbnail";
import { thumbnailQueue } from "../../services/thumbnails";
import { thumbnailKey } from "../../services/thumbnailQueue";
import { useTheme } from "../../contexts/ThemeContext";
import MapAttribution from "../common/MapAttribution";
import type { MapView } from "../../utils/areaThumbnail";

const MapIcon = IoMapOutline as any;

const WIDTH = 160;
const HEIGHT = 96;
/** A project or dashboard draws at most this many of its areas. */
const MAX_AREAS = 12;
/** An area is asked for at most this many ids: a thumbnail draws an outline, not every cell. */
const MAX_IDS = 50;

/** True once the element has come within 200px of the viewport. Without IntersectionObserver (jsdom) it counts as in view. */
function useInView(ref: React.RefObject<Element | null>): boolean {
  const [inView, setInView] = useState(typeof IntersectionObserver === "undefined");
  useEffect(() => {
    const el = ref.current;
    if (inView || !el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, inView]);
  return inView;
}

/** The pixel size of the element, once measured; falls back to the default (jsdom measures 0). */
function useBoxSize(ref: React.RefObject<HTMLElement | null>, active: boolean): { w: number; h: number } {
  const [size, setSize] = useState({ w: WIDTH, h: HEIGHT });
  useEffect(() => {
    const el = ref.current;
    if (!active || !el) return;
    const measure = () => {
      const w = Math.round(el.clientWidth);
      const h = Math.round(el.clientHeight);
      if (w > 0 && h > 0) setSize((s) => (s.w === w && s.h === h ? s : { w, h }));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, active]);
  return size;
}

interface AreaThumbnailProps {
  /** One area for an area card; the member areas for a project or dashboard. */
  areas: AreaLike[];
  /** Accessible name of the picture. */
  label: string;
  className?: string;
}

/**
 * The Carto basemap image for the view, rendered by the shared offscreen map. Reads the cache first (no flash on
 * remount). While a new image is on its way (theme switch) the previous one of the same areas stays. A failure is
 * remembered for this mount and key, so it is not retried in a loop.
 */
function useBasemapImage(
  areaKey: string,
  view: MapView | null,
  dark: boolean,
  w: number,
  h: number,
): { src: string | null; failed: boolean } {
  const key = view ? thumbnailKey(areaKey.split("|"), dark, w, h) : null;
  const base = `${areaKey}::${w}x${h}`;
  const [loaded, setLoaded] = useState<{ key: string; base: string; src: string } | null>(null);
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const jobRef = useRef({ view, dark, w, h });
  jobRef.current = { view, dark, w, h };

  const cached = key ? thumbnailQueue.peek(key) : undefined;
  const hasView = view !== null;

  useEffect(() => {
    if (!key || !hasView || thumbnailQueue.peek(key) !== undefined) return;
    const { view: v, dark: d, w: width, h: height } = jobRef.current;
    if (!v) return;
    let live = true;
    const { promise, release } = thumbnailQueue.request({
      key,
      view: { center: v.center, zoom: v.zoom },
      dark: d,
      width,
      height,
    });
    promise.then(
      (src) => live && setLoaded({ key, base, src }),
      () => live && setFailedKey(key),
    );
    return () => {
      live = false;
      release();
    };
  }, [key, base, hasView]);

  const failed = key !== null && failedKey === key && cached === undefined;
  let src: string | null = null;
  if (!failed) {
    if (cached !== undefined) src = cached;
    else if (loaded && loaded.key === key) src = loaded.src;
    else if (loaded && loaded.base === base) src = loaded.src; // theme switch: keep the old image until the new one arrives
  }
  return { src, failed };
}

const BOX = "relative h-28 w-full overflow-hidden bg-paper-200";

/**
 * A small outline of an area (or of several), drawn from its stored geometry as an SVG over a Carto vector basemap
 * image. The outline is drawn by this component and shows at once; the basemap is rendered by a shared offscreen
 * map (services/thumbnails) and fades in beneath it, and if it cannot be rendered the outline stays on neutral. It
 * loads only once it is near the screen, the shapes are cached per area, and the box has a fixed height, so a grid
 * never jumps or waits for it. Without a shape it shows a neutral placeholder.
 */
const AreaThumbnail: React.FC<AreaThumbnailProps> = ({ areas, label, className = "" }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref);
  const patternId = `dots-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const { isDark } = useTheme();
  const [imageReady, setImageReady] = useState(false);
  const used = areas.slice(0, MAX_AREAS);
  const areaKey = used.map((a) => a.id).join("|");

  // Asked for only once the card is near the screen. A failure shows the placeholder and is not kept, so the
  // next time a card for the area mounts it asks again.
  const { shapes: areaShapes, status } = useAreaShapes(used, { enabled: inView, maxIds: MAX_IDS });
  const shapes = useMemo(() => (status === "loading" ? null : areaShapes.flatMap(shapesFromGeoJSON)), [status, areaShapes]);

  const { w, h } = useBoxSize(ref, shapes !== null);
  const fit = useMemo(() => (shapes ? thumbnailView(shapes, w, h) : null), [shapes, w, h]);
  const { src } = useBasemapImage(areaKey, fit, isDark, w, h);

  if (used.length > 0 && shapes === null) {
    return (
      <div ref={ref} data-testid="area-thumbnail-loading" className={`${BOX} animate-pulse ${className}`}>
        <MapIcon aria-hidden="true" className="absolute left-1/2 top-1/2 h-7 w-7 -translate-x-1/2 -translate-y-1/2 text-ink-faint" />
      </div>
    );
  }

  if (!fit) {
    return (
      <div ref={ref} className={`${BOX} ${className}`}>
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          preserveAspectRatio="xMidYMid slice"
          role="img"
          aria-label={label}
          data-testid="area-thumbnail-placeholder"
          className="absolute inset-0 h-full w-full"
        >
          <defs>
            <pattern id={patternId} width="8" height="8" patternUnits="userSpaceOnUse">
              <circle cx="4" cy="4" r="0.8" className="fill-paper-300" />
            </pattern>
          </defs>
          <rect width={WIDTH} height={HEIGHT} fill={`url(#${patternId})`} />
        </svg>
        <MapIcon aria-hidden="true" className="absolute left-1/2 top-1/2 h-7 w-7 -translate-x-1/2 -translate-y-1/2 text-ink-faint" />
      </div>
    );
  }

  return (
    <div ref={ref} className={`${BOX} ${className}`}>
      {src && (
        <img
          data-testid="area-thumbnail-basemap"
          src={src}
          alt=""
          aria-hidden="true"
          draggable={false}
          onLoad={() => setImageReady(true)}
          className={`absolute inset-0 h-full w-full select-none transition-opacity duration-200 ${imageReady ? "opacity-100" : "opacity-0"}`}
        />
      )}
      <svg
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label={label}
        className="absolute inset-0 h-full w-full"
      >
        {fit.paths.map((p, i) =>
          p.kind === "polygon" ? (
            <path
              key={i}
              data-testid="area-thumbnail-path"
              d={p.d}
              className="fill-brand-500/20 stroke-brand-600"
              strokeWidth={1}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ) : (
            <path
              key={i}
              data-testid="area-thumbnail-path"
              d={p.d}
              className="fill-none stroke-brand-600"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ),
        )}
      </svg>
      {src && <MapAttribution variant="thumbnail" />}
    </div>
  );
};

export default AreaThumbnail;
